import useLadyService from '@/services/LadyService';
import './citySlider.scss';
import { useRef, useCallback, useEffect, useState, useMemo } from 'react';
import { Icon, Select } from '@/components/ui';
import { useDispatch, useSelector } from 'react-redux';
import { getCountriesData } from '@/stores/slices/userSlice';
import { useActiveCountry, useToggleCountry } from '../ui/select/SelectCountry';


const CitySlider = () => {
    const { t, navigate, lang } = useLadyService();
    const dispatch = useDispatch();
    const countriesData = useSelector(getCountriesData);
    // const toggleCountry = useToggleCountry()
    const activeCountry = useActiveCountry();



    // [{ cz: {...} }, { sk: {...} }] -> [{ code: 'cz', country: {...} }, ...]
    const countryEntries = useMemo(() => countriesData?.length
        ? countriesData.flatMap((item) => Object.entries(item))
        : [], [countriesData])


    const country_options = useMemo(() => countryEntries.map(([code, country]) => ({
        value: code ?? 'cz',
        title: country.name ?? 'Czechia',
        icon: ` icon-flag ${code.toUpperCase() ?? 'CZ'}`,
        ads: country.profiles_count ?? 0
    })), [countryEntries]);

    const [selectedCountry, setSelectedCountry] = useState(() => {
        const fromUrl = country_options.find((o) => o.value === activeCountry?.code);
        return fromUrl
            ?? country_options[0]
            ?? { value: 'cz', title: 'Czechia', icon: ' icon-flag CZ', ads: 0 };
    });


    useEffect(() => {
        const code = activeCountry?.code;
        // Sync only when the URL's country actually changes — so
        // - data (re)loads don't wipe the user's manual dropdown choice,
        // - clicking a city in the slider (which navigates) updates the flag.
        if (!code) return;
        const option = country_options.find((o) => o.value === code);
        if (option) setSelectedCountry(option);
    }, [activeCountry?.code, country_options]);

    const calculateStyle = (idx, i) => {
        const cols = 10;
        const spriteW = 2000;
        const spriteH = 450;
        const rows = 3

        const col = idx % cols;
        const row = Math.floor(idx / cols);
        const posX = col * (spriteW / cols);
        const posY = row * (spriteH / rows) + 20;
        const classes = ` city-${i.slug}`
        const styles = { 'background-position': `-${posX.toFixed(2)}px -${posY.toFixed(2)}px` }

        return { styles, classes }
    }

    const cities = useMemo(() => (countryEntries
        .find(([code]) => code === selectedCountry?.value)?.[1]
        ?.cities ?? []).map((c, idx) => {
            const { styles, classes } = calculateStyle(idx, c);

            return { ...c, styles, classes }
        }), [countryEntries, selectedCountry]);

    const setCountry = (value) => {
        const found = country_options.find((i) => i?.value === value);
        setSelectedCountry(found ?? { value: '', title: '-', icon: '-' });
        const entry = countryEntries.find(([code]) => code === value);
        const slug = entry?.[1]?.slug || value;
        navigate(`/${lang}/${slug}`);
    };

    const handleCityChange = (slug) => navigate(`/${lang}/${slug}`);

    const trackRef = useRef(null);
    const [edges, setEdges] = useState({ start: true, end: false });
    const [visibleCities, setVisibleCities] = useState([]);

    const updateEdges = useCallback(() => {
        const el = trackRef.current;
        if (!el) return;

        const max = el.scrollWidth - el.clientWidth;

        setEdges({
            start: el.scrollLeft <= 1,
            end: el.scrollLeft >= max - 1,
        });

        const left = el.getBoundingClientRect().left + el.clientLeft;
        const right = left + el.clientWidth;

        const cards = el.querySelectorAll('.city--item:not(.country-block)');

        const visible = Array.from(cards).flatMap((card, index) => {
            const rect = card.getBoundingClientRect();
            const visibleWidth = Math.max(
                0,
                Math.min(rect.right, right) - Math.max(rect.left, left)
            );

            return visibleWidth >= rect.width / 2 ? [index] : [];
        });

        setVisibleCities(visible);
    }, []);

    const scrollToCity = (index) => {
        const el = trackRef.current;
        const card = el?.querySelectorAll(
            '.city--item:not(.country-block)'
        )[index];

        if (!card) return;

        // Center the selected card in the slider.
        const left =
            el.scrollLeft +
            card.getBoundingClientRect().left -
            (el.getBoundingClientRect().left + el.clientLeft) -
            (el.clientWidth - card.offsetWidth) / 2;

        el.scrollTo({ left, behavior: 'smooth' });
    };

    useEffect(() => {
        const el = trackRef.current;
        if (!el) return;

        updateEdges();
        el.addEventListener('scroll', updateEdges, { passive: true });
        window.addEventListener('resize', updateEdges);

        return () => {
            el.removeEventListener('scroll', updateEdges);
            window.removeEventListener('resize', updateEdges);
        };
    }, [updateEdges, cities]);

    const scrollByPage = (direction) => {
        const el = trackRef.current;
        el?.scrollBy({ left: direction * el.clientWidth, behavior: 'smooth' });
    };

    return (
        <>
            <div className='cities-wrapper'>
                <button
                    className="cities-slider__arrow button button--secondary cities-slider__arrow--prev"
                    onClick={() => scrollByPage(-1)}
                    disabled={edges.start}
                    aria-label="Previous cities"
                >
                    ‹
                </button>
                <ul className="cities" ref={trackRef}>
                    <li className={`city--item country-block`}>
                        <Select
                            name={'country_select'}
                            onChange={setCountry}
                            options={country_options}
                            arrowSize={'m'}
                            withoutTitle={true}
                            withIcon={true}
                            defaulTitle={t('country')}
                            clazzWrapper={'select-field m-h-8'}
                            controlledValue={selectedCountry?.value}
                            controlledTitle={selectedCountry?.title}
                            controlledIcon={selectedCountry?.icon}
                            clazzSvg={'color-green'}
                        />
                        <hr />
                        <div className='city--info'>
                            <span className='title_h3'>
                                {`${t('active')} ${t('profiles')}: `}
                            </span>
                            <b className='d-flex align-center gap-8'><Icon spritePath='pants' clazz='color-main' />{selectedCountry?.ads}</b>
                        </div>
                    </li>
                    {cities.map(({ slug, name, profiles_count, classes, styles }, i) => (
                        <li key={`${slug}-${i}`} className={`city--item cursor-pointer`} onClick={() => handleCityChange(slug)}>
                            <div style={styles} className={`img-placeholder city ${classes}`} />
                            <div className='city--info'>
                                <span className='title_h3'>
                                    {name}
                                </span>
                                <p className='small'>
                                    <Icon spritePath='pants' clazz='color-main' />
                                    {profiles_count}
                                    {/* {t('profiles')} */}
                                </p>
                            </div>
                        </li>
                    ))}

                </ul>
                <button
                    className="cities-slider__arrow button button--secondary cities-slider__arrow--next"
                    onClick={() => scrollByPage(1)}
                    disabled={edges.end}
                    aria-label="Next cities"
                >
                    ›
                </button>
            </div>
            <div className="cities--pagination">
                {cities.map((city, index) => (
                    <span
                        onClick={() => scrollToCity(index)}
                        key={`city-dot-${city.slug}-${index}`}
                        className={`pagination-dot ${visibleCities.includes(index) ? 'active' : ''
                            }`}
                    />
                ))}
            </div>
        </>


    )
};

export default CitySlider;